import { useEffect, useRef } from "react";
import jwtDecode from "jwt-decode";
import { AutenticacaoModule } from "src/Contexto/auth";

interface ITokenDecode {
  exp: number;
}

const REFRESH_BUFFER_MS = 60 * 1000; // 1 minuto antes de expirar
const MIN_DELAY_MS = 5 * 1000; // Mínimo de 5 segundos
const MAX_TIMEOUT_MS = 2147483647; // Limite de 32-bit para setTimeout

function RefreshTokenComponent({ children }: any) {
  const { user, refreshToken, isLogged } = AutenticacaoModule();
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (!isLogged || !user?.token_access) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    const executeRefresh = async () => {
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      try {
        await refreshToken();
      } finally {
        isRefreshingRef.current = false;
      }
    };

    const scheduleRefresh = () => {
      try {
        const decoded = jwtDecode<ITokenDecode>(user.token_access);
        if (decoded?.exp) {
          const expiresInMs = decoded.exp * 1000 - Date.now();
          const targetDelay = Math.max(expiresInMs - REFRESH_BUFFER_MS, MIN_DELAY_MS);
          const safeDelay = Math.min(targetDelay, MAX_TIMEOUT_MS);

          timeoutId = setTimeout(() => {
            executeRefresh();
          }, safeDelay);
        }
      } catch (error) {
        // Fallback: se não conseguir decodificar o exp, tenta renovar após 9 minutos
        timeoutId = setTimeout(() => {
          executeRefresh();
        }, 9 * 60 * 1000);
      }
    };

    const checkAndRefreshIfNeeded = () => {
      if (!user?.token_access) return;
      try {
        const decoded = jwtDecode<ITokenDecode>(user.token_access);
        if (decoded?.exp) {
          const remainingMs = decoded.exp * 1000 - Date.now();
          if (remainingMs <= REFRESH_BUFFER_MS) {
            executeRefresh();
          }
        }
      } catch {
        // Ignora erro de parse
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAndRefreshIfNeeded();
      }
    };

    const handleFocus = () => {
      checkAndRefreshIfNeeded();
    };

    scheduleRefresh();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [user, isLogged, refreshToken]);

  return children;
}

export default RefreshTokenComponent;

